import React, { useState } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import dayjs from "dayjs";
import MaterialInput from "../../../components/MaterialInput";
import MaterialBox from "../../../components/MaterialBox";
import MaterialTypography from "../../../components/MaterialTypography";
import MaterialButton from "../../../components/MaterialButton";
import Grid from "@mui/material/Grid2";
import axios from "axios";
import ConfirmationDialog from "../../../components/Dialog/ConfirmationDialog";
import BASEURL from "../../Utils/Baseurl";

const AttendancePDF = () => {
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(
    dayjs().format("YYYY-MM-DD")
  );
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: "",
    message: "",
    showCancel: true,
    showConfirm: true,
    customSection: null,
    onConfirm: () => {},
    onCancel: () => {},
  });

  const fetchDailyAttendanceReport = async () => {
    try {
      const response = await axios.post(
        BASEURL,
        {
          query: `
            query DailyAttendanceReport($date: Date!) {
              dailyAttendanceReport(date: $date) {
                id
                empid
                firstName
                lastName
                shift
                date
                checkIn
                checkOut
                totalWorkingHours
                overtime
                status
                presentCount
                absentCount
              }
            }
          `,
          variables: {
            date: selectedDate,
          },
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer YOUR_TOKEN_HERE`,
          },
        }
      );

      const data = response.data.data.dailyAttendanceReport;

      if (data.length > 0) {
        handleDownloadPdf(data);
      } else {
        setOpen(false);
        setConfirmationDialog({
          open: true,
          title: "Notice",
          message: "No records were found for the selected date.",
          showCancel: true,
          showConfirm: false,
          onCancel: () => {
            setConfirmationDialog({
              ...confirmationDialog,
              open: false,
            });
          },
        });
      }
    } catch (error) {
      console.error("Error fetching Daily Attendance Report:", error);
      setOpen(false);
      setConfirmationDialog({
        open: true,
        title: "Error message",
        message:
          "Sorry for your inconvience, there is some issue in our network. Please try again later.",
        showCancel: true,
        showConfirm: false,

        onCancel: () => {
          setConfirmationDialog({
            ...confirmationDialog,
            open: false,
          });
        },
      });
    }
  };

  const handleDownloadPdf = (data) => {
    const doc = new jsPDF();
    const today = dayjs().format("DD MMM YYYY HH:mm");
    const pageWidth = doc.internal.pageSize.getWidth();

    doc.setFontSize(14);
    const title = "Daily Attendance Report (Summary Report)";
    const textWidth = doc.getTextWidth(title);
    const centerX = (pageWidth - textWidth) / 2;
    doc.text(title, centerX, 15);

    doc.setFontSize(10);
    doc.text(`Company: EMF`, 14, 30);
    doc.text(`Printed On: ${today}`, pageWidth - 14, 30, { align: "right" });

    doc.setLineWidth(0.5);
    doc.line(14, 34, pageWidth - 14, 34);

    let finalY = 40;

    data.forEach((employee, index) => {
      const employeeName = `${employee.firstName} ${employee.lastName}`;
      const date = dayjs(employee.date).format("DD-MMM-YYYY");
      const inTime = dayjs(employee.checkIn).format("HH:mm");
      const outTime = employee.checkOut
        ? dayjs(employee.checkOut).format("HH:mm")
        : "";
      const totalDuration = employee.totalWorkingHours || "0:00";

      doc.setFontSize(10);
      finalY += 6;
      doc.text(
        `Employee Code: ${employee.empid}   Employee Name: ${employeeName}`,
        14,
        finalY
      );
      finalY += 4;

      const body = [
        [
          date,
          inTime,
          outTime,
          employee.shift,
          totalDuration,
          employee.status,
          "",
        ],
      ];

      autoTable(doc, {
        startY: finalY + 2,
        head: [
          [
            "Date",
            "InTime",
            "OutTime",
            "Shift",
            "Total Duration",
            "Status",
            "Remarks",
          ],
        ],
        body,
        theme: "grid",
        headStyles: {
          fillColor: [200, 200, 200],
          textColor: 20,
          halign: "center",
        },
        styles: { fontSize: 9 },
        margin: { left: 14, right: 14 },
      });

      finalY = doc.lastAutoTable.finalY + 4;

      const summaryText = `Total Duration=${totalDuration} , PresentDays=${employee.presentCount}, Leaves=0, Holiday=0, AbsentDays=${employee.absentCount}, Weekly Off=0`;
      doc.setFont("helvetica", "bold");
      doc.text(summaryText, 14, finalY);
      doc.setFont("helvetica", "normal");
      finalY += 10;

      if (finalY > 270 && index !== data.length - 1) {
        doc.addPage();
        finalY = 20;
      }
    });

    doc.save(`Daily_Attendance_Report_${selectedDate}.pdf`);
    setOpen(false);
  };

  return (
    <div>
      <button
        onClick={() => setOpen(true)}
        style={{
          marginBottom: "10px",
          padding: "10px 12px",
          background: "#f70000ff",
          color: "#fff",
          border: "none",
          cursor: "pointer",
        }}
      >
        Daily Attendance Report
      </button>
      {confirmationDialog.open && (
        <ConfirmationDialog
          open={confirmationDialog.open}
          title={confirmationDialog.title}
          message={confirmationDialog.message}
          showCancel={confirmationDialog.showCancel}
          showConfirm={confirmationDialog.showConfirm}
          customSection={confirmationDialog.customSection}
          onConfirm={confirmationDialog.onConfirm}
          onCancel={confirmationDialog.onCancel}
        />
      )}
      <Dialog open={open}>
        <DialogTitle>
          Select Date to Download Daily Attendance Report
        </DialogTitle>
        <DialogContent>
          <Grid container>
            <Grid size={{ xs: 12 }}>
              <MaterialBox lineHeight={0} display="inline-block">
                <MaterialTypography
                  component="label"
                  variant="caption"
                  fontWeight="bold"
                >
                  Choose Date
                </MaterialTypography>
              </MaterialBox>
              <MaterialInput
                label="Report Date"
                type="date"
                fullWidth
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: new Date().toISOString().split("T")[0] }}
                sx={{ marginTop: 1 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <MaterialButton onClick={() => setOpen(false)}>Cancel</MaterialButton>
          <MaterialButton
            variant="gradient"
            color="dark"
            onClick={fetchDailyAttendanceReport}
          >
            Download
          </MaterialButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default AttendancePDF;
